import axios, {
	AxiosError,
	type AxiosInstance,
	type AxiosRequestConfig,
	type InternalAxiosRequestConfig,
} from 'axios';
import { IP_NOT_ALLOWED, normalizeApiError } from './httpError';
import type { NormalizedApiError } from './types';

declare module 'axios' {
	export interface AxiosRequestConfig {
		/** Skip the automatic 401 refresh-and-retry flow (login, refresh itself). */
		skipAuthRefresh?: boolean;
		/** Internal marker so a request is only retried once after a refresh. */
		_retry?: boolean;
	}
}

export const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? '/api';
export const API_TIMEOUT: number = Number(
	import.meta.env.VITE_API_TIMEOUT ?? 15000,
);

/**
 * Session wiring of one authenticated client. Read on every request, so the
 * owner can swap the handlers at any time (the session store registers them
 * after the client exists).
 */
export interface SessionHooks {
	getAccessToken: () => string | null;
	/**
	 * Renew the session. Resolves to a new access token, or to `null` when the
	 * session cannot be renewed (no refresh token / rejected).
	 */
	refresh: () => Promise<string | null>;
	/** Called once the session is no longer recoverable. */
	onAuthFailure: () => void;
	/**
	 * Called when the tenant's CMS IP allowlist refuses a request (403
	 * IP_NOT_ALLOWED). The session itself is still valid, so it is neither
	 * refreshed nor cleared.
	 */
	onAccessBlocked?: (error: NormalizedApiError) => void;
}

export interface AuthenticatedClient {
	client: AxiosInstance;
	/** Typed request helper returning the response body directly. */
	request: <T>(config: AxiosRequestConfig) => Promise<T>;
}

/**
 * HTTP client bound to one session (the CMS user, or the customer portal).
 * Attaches the bearer token, refreshes once on a 401 (concurrent 401s share a
 * single refresh), and normalizes every error.
 */
export function createAuthenticatedClient(
	hooks: SessionHooks,
): AuthenticatedClient {
	const client = axios.create({
		baseURL: API_BASE_URL,
		timeout: API_TIMEOUT,
		headers: { 'Content-Type': 'application/json' },
	});

	client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
		const token = hooks.getAccessToken();
		if (token) config.headers.Authorization = `Bearer ${token}`;
		return config;
	});

	let refreshPromise: Promise<string | null> | null = null;

	const refreshOnce = (): Promise<string | null> => {
		if (!refreshPromise) {
			refreshPromise = hooks
				.refresh()
				.catch(() => null)
				.finally(() => {
					refreshPromise = null;
				});
		}
		return refreshPromise;
	};

	client.interceptors.response.use(
		(response) => response,
		async (error: AxiosError) => {
			const original = error.config as InternalAxiosRequestConfig | undefined;

			if (
				error.response?.status === 401 &&
				original &&
				!original.skipAuthRefresh
			) {
				if (!original._retry) {
					original._retry = true;
					const token = await refreshOnce();

					if (token) {
						original.headers.Authorization = `Bearer ${token}`;
						return client.request(original);
					}
				}

				// Refresh unavailable, failed, or the retried request was rejected again.
				hooks.onAuthFailure();
			}

			const normalized = normalizeApiError(error);
			if (normalized.status === 403 && normalized.code === IP_NOT_ALLOWED) {
				hooks.onAccessBlocked?.(normalized);
			}

			return Promise.reject(normalized);
		},
	);

	return {
		client,
		request: async <T>(config: AxiosRequestConfig): Promise<T> => {
			const response = await client.request<T>(config);
			return response.data;
		},
	};
}
